import React from "react";
import images from "../../../utilities/images/images";
import {
  formatPrice,
  getDiscountPercent,
  getFinalAmount,
  hasPackageDiscount,
} from "../utils/coursePricing";
import { isVariantPackage, isPackagePurchased, shouldShowBuyNow } from "../utils/explorePackage";
import { getExploreCourseAnchorId } from "../utils/exploreCoursesScrollRestore";
import { getExploreCardClassName } from "../utils/exploreCardHighlight";
import DashboardBenefitTick from "./DashboardBenefitTick";
import PackageBottomNote from "./PackageBottomNote";
import "./DashboardBenefitTick.css";
import "./PackageBottomNote.css";
import "./dashboardExploreCard.css";
import "./CourseCard.css";

const CourseCard = ({
  course,
  section = "main",
  highlightedAnchorId = null,
  onDetails,
  onBuy,
  onOpenVariants,
}) => {
  const finalAmount = getFinalAmount(course?.amount, course?.discount);
  const showDiscount = hasPackageDiscount(course?.amount, course?.discount);
  const discountLabel = getDiscountPercent(course?.amount, course?.discount);
  const isPurchased = isPackagePurchased(course);
  const showBuyNow = shouldShowBuyNow(course);
  const isVariant = isVariantPackage(course);

  const metaTags = [course?.duration, course?.course].filter(Boolean);

  const features = Array.isArray(course?.course_benefits)
    ? course.course_benefits.slice(0, 3)
    : [];

  const anchorId = course?.package_id
    ? getExploreCourseAnchorId(course.package_id, section)
    : undefined;
  const isSelected = Boolean(anchorId && highlightedAnchorId === anchorId);

  return (
    <article
      className={getExploreCardClassName("dashboard-course-card", anchorId, highlightedAnchorId)}
      id={anchorId}
      aria-current={isSelected ? "true" : undefined}
    >
      <div className="dashboard-course-card__banner">
        <img
          src={course?.icon}
          alt=""
          className="dashboard-course-card__banner-image"
        />
        {course?.thumbnail_tag ? (
          <span
            className="dashboard-course-card__banner-tag"
            style={{
              backgroundColor: course?.tag_background || "#ffaf51",
              color: course?.tag_textcolor || "#000000",
            }}
          >
            {course.thumbnail_tag}
          </span>
        ) : null}
      </div>

      <div className="dashboard-course-card__body">
        <div className="dashboard-course-card__content">
          <div className="dashboard-course-card__head">
            {metaTags.length ? (
              <div className="dashboard-course-card__tags">
                {metaTags.map((tag) => (
                  <span key={tag} className="dashboard-course-card__tag">
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}

            <div className="dashboard-course-card__title-row">
              <h3 className="dashboard-course-card__title">
                {course?.package_name || "—"}
              </h3>
            </div>

            {features.length ? (
              <ul className="dashboard-benefit-list dashboard-benefit-list--course dashboard-course-card__features">
                {features.map((feature) => (
                  <li key={feature} className="dashboard-benefit-list__item">
                    <DashboardBenefitTick />
                    <span className="dashboard-benefit-list__text">{feature}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <div className="dashboard-course-card__price-row">
            <div className="dashboard-course-card__prices">
              <strong>{formatPrice(finalAmount)}</strong>
              {showDiscount ? (
                <span className="dashboard-course-card__strike">
                  {formatPrice(course.amount)}
                </span>
              ) : null}
            </div>
            {discountLabel ? (
              <span className="dashboard-course-card__discount">{discountLabel}</span>
            ) : null}
          </div>

          <div
            className={`dashboard-course-card__actions${
              isPurchased ? " dashboard-course-card__actions--purchased" : ""
            }`}
          >
            {isPurchased ? (
              <button
                type="button"
                className="dashboard-course-card__btn dashboard-course-card__btn--primary"
                onClick={onDetails}
              >
                View Course
              </button>
            ) : (
              <button
                type="button"
                className="dashboard-course-card__btn dashboard-course-card__btn--outline"
                onClick={onDetails}
              >
                Course details
              </button>
            )}
            {!showBuyNow ? null : (
              <button
                type="button"
                className="dashboard-course-card__btn dashboard-course-card__btn--primary"
                onClick={onBuy}
              >
                Buy Now
                {isVariant ? (
                  <img
                    src={images.downArrow}
                    alt=""
                    className="dashboard-course-card__btn-icon"
                    aria-hidden="true"
                  />
                ) : null}
              </button>
            )}
          </div>
        </div>

        <PackageBottomNote
          course={course}
          onOpenVariants={onOpenVariants}
          className="dashboard-course-card__bottom-note"
        />
      </div>
    </article>
  );
};

export default CourseCard;
